import { BaseTrigger } from './trigger/BaseTrigger';
import { RecurringTrigger } from './trigger/RecurringTrigger';
import { DateTimeUtil } from '../../common/DateTimeUtil';
import * as InternalTypes from '../../common/InternalTypes';
import { LoggerTexts } from '../../common/LoggerTexts';
import { Util } from '../../common/Util';
import { AppPushState } from '../../data/AppPushState';

export class AppPushConfig {
	private _areaId: string;
	private _triggerName: string;
	private _validFrom: number;
	private _validTo: number;
	private _isEnabled: boolean;
	private _triggerType: string;
	private _triggerConfig: BaseTrigger | RecurringTrigger;

	init(areaId: string, triggerName: string, validFrom: string, validTo: string, isEnabled: boolean) {
		if (Util.stringIsEmpty(areaId)) {
			throw Error(LoggerTexts.APP_PUSH_CFG.AREAID_CANNOT_BE_EMTPY);
		}
		this._areaId = areaId;
		if (Util.stringIsEmpty(triggerName)) {
			throw Error(LoggerTexts.APP_PUSH_CFG.TRIGGERNAME_CANNOT_BE_EMTPY);
		}
		this._triggerName = triggerName;
		if (!DateTimeUtil.isDateValidInISO(validFrom)) {
			throw Error(`Valid-From(${validFrom}) is not a valid date for ${areaId} ${triggerName}`);
		}
		if (!DateTimeUtil.isDateValidInISO(validTo)) {
			throw Error(`Valid-To(${validTo}) is not a valid date for ${areaId} ${triggerName}`);
		}
		const formattedValidFrom = DateTimeUtil.getTimeFromISODate(validFrom);
		const formattedValidTo = DateTimeUtil.getTimeFromISODate(validTo);
		if (formattedValidFrom >= formattedValidTo) {
			throw Error(LoggerTexts.APP_PUSH_CFG.VALID_FROM_CANNOT_BE_AFTER_VALID_TO);
		}
		this._validFrom = formattedValidFrom;
		this._validTo = formattedValidTo;
		this._isEnabled = isEnabled;
	}

	initFromJsonObject(appPushConfig: InternalTypes.AppPushConfig) {
		const { areaId, triggerName, validFrom, validTo, isEnabled } = appPushConfig;
		this.init(areaId, triggerName, validFrom, validTo, isEnabled);
	}

	get combinedKey(): string {
		return Util.getCombinedKey(this._areaId, this._triggerName);
	}

	get areaId(): string {
		return this._areaId;
	}

	get triggerName(): string {
		return this._triggerName;
	}

	get validFrom(): number {
		return this._validFrom;
	}

	get validTo(): number {
		return this._validTo;
	}

	get isEnabled(): boolean {
		return this._isEnabled;
	}

	set triggerType(triggerType: string) {
		this._triggerType = triggerType;
	}

	get triggerType(): string {
		return this._triggerType;
	}

	set triggerConfig(triggerConfig: BaseTrigger | RecurringTrigger) {
		this._triggerConfig = triggerConfig;
	}

	get triggerConfig(): BaseTrigger | RecurringTrigger {
		return this._triggerConfig;
	}

	isQualifiedForPush(appPushState: AppPushState): boolean {
		return this.triggerConfig.isQualifiedForPush(appPushState);
	}

	isActive(): boolean {
		if (this.isEnabled && !this.isExpired()) {
			return true;
		}
		return false;
	}

	isExpired(): boolean {
		const now = Date.now();
		if (this._isValidNow(now) || this._isValidInFuture(now)) {
			return false;
		}
		return true;
	}

	private _isValidNow(timeToCompare: number): boolean {
		if (this.validFrom < timeToCompare && this.validTo > timeToCompare) {
			return true;
		}
		return false;
	}

	private _isValidInFuture(timeToCompare: number): boolean {
		if (this.validFrom > timeToCompare && this.validTo > timeToCompare && this.validTo > this.validFrom) {
			return true;
		}
		return false;
	}
}
